import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeCatalogEth, updateCatalogEth } from "redux/actions";
import styled from "styled-components";
import * as yup from "yup";
import ImageDropzone from "components/ImageDropzone";
import { InputSelect } from "components/Form/InputSelect";
import { getRole } from "hooks";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

// const GridWrapper = styled(Box)`
//   display: grid;
//   grid-template-columns: 1fr 1fr;
//   gap: 12px;
// `;

interface Props {
  catalogId: string;
}

interface ValueSchema {
  label: string;
  value: string | number;
}

const initialValues = {
  catalogName: "",
  pointsNeeded: "",
  expiryDate: "",
  catalogType: "",
  catalogProduct: "",
  pointTypeUsed: "",
};

const CatalogEthFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  catalogId,
}) => {
  const isAddMode = !catalogId;
  const dispatch = useDispatch();
  const userRole = getRole();
  const formikRef = useRef<FormikValues>() as any;

  const [tenant, setTenant] = useState<ValueSchema | null>(null);
  const [filesCatalogImage, setFilesCatalogImage] = useState<any>([]);

  const [tenantItems] = React.useState([
   { label: "Ancol Points", value: 3 },
  ]);

  const { appUrl } = useSelector((state: any) => state.globalReducer);
  const { catalogsEth, isFormLoading } = useSelector(
    (state: any) => state.catalogEthReducer
  );

  const handleCloseModal = () => {
    onClose && onClose();
    setTenant(null);
    setFilesCatalogImage([]);
  };

  const handleSubmit = (values: any) => {
    const { catalogName, catalogProduct, tenantId } = values;
    const catalogImage =
      typeof filesCatalogImage[0] === "object" ? filesCatalogImage[0] : null;

    isAddMode
      ? dispatch(
          storeCatalogEth(
            {
              catalogName,
              catalogProduct,
              catalogImage,
              tenantId,
            },
            handleCloseModal
          ) as any
        )
      : dispatch(
          updateCatalogEth(
            catalogId,
            {
              catalogName,
              catalogProduct,
              catalogImage,
              tenantId,
            },
            handleCloseModal
          ) as any
        );
  };

  useEffect(() => {
    if (!isAddMode) {
      const catalogEthDetail = catalogsEth.find(
        (catalog: any) => catalog.id === parseInt(catalogId)
      );

      if (formikRef.current) {
        formikRef.current.setFieldValue(
          "catalogName",
          catalogEthDetail.catalog_name
        );
        formikRef.current.setFieldValue(
          "catalogProduct",
          catalogEthDetail.catalog_product
        );
        formikRef.current.setFieldValue("tenantId", catalogEthDetail.tenant_id);

        tenantItems.map((item) =>
          item.value.toString() === catalogEthDetail.tenant_id
            ? setTenant(item)
            : null
        );
        setFilesCatalogImage([`${appUrl}/storage/${catalogEthDetail.img_url}`]);
      }
    }
  }, [isAddMode, catalogsEth, catalogId, appUrl]);

  const validationSchema = yup.object({
    catalogName: yup.string().required("Catalog Name is required field"),
    catalogProduct: yup.string().required("Catalog Product is required field"),
    tenantId:
      userRole !== "tenant"
        ? yup.string().required("Tenant is required field")
        : yup.string(),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue }) => {
          return (
            <Form>
              <DialogHeader>
                <Text fontWeight={700}>
                  {isAddMode ? "Add Ethereum" : "Update Ethereum"}
                </Text>
              </DialogHeader>
              <DialogContent>
                <FormWrapper>
                  <Field name="catalogName">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="catalogName">Ethereum</FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  <Field name="catalogProduct">
                    {({ field, meta }: FieldProps<string>) => (
                      <Stack spacing="xs">
                        <FormLabel htmlFor="catalogProduct">
                          Catalog Product
                        </FormLabel>
                        <InputGroup inputSize={40}>
                          <InputText
                            type="text"
                            size={40}
                            errors={!!meta.error && meta.touched}
                            {...field}
                          />
                        </InputGroup>
                        {!!meta.error && meta.touched && (
                          <InputMessage variant="error">
                            {meta.error}
                          </InputMessage>
                        )}
                      </Stack>
                    )}
                  </Field>
                  {userRole !== "tenant" ? (
                    <Field name="tenantId">
                      {({ field, meta }: FieldProps<string>) => (
                        <Stack spacing="xs">
                          <FormLabel htmlFor="tenantId">Tenant</FormLabel>
                          <InputGroup inputSize={40}>
                            <InputSelect
                              selectedItem={tenant}
                              width={"100%"}
                              placeholder="Select Tenant"
                              handleSelectedItemChange={({ selectedItem }) => {
                                if (selectedItem) {
                                  setTenant(selectedItem);
                                  setFieldValue("tenantId", selectedItem.value);
                                }
                              }}
                              itemToString={(item) =>
                                item ? `${item.label}` : ""
                              }
                              itemValue={(item) =>
                                item ? `${item.value}` : ""
                              }
                              items={tenantItems}
                              size={"lg"}
                              borderRadius={"6px"}
                              errors={!!meta.error && meta.touched}
                            />
                          </InputGroup>
                          {!!meta.error && meta.touched && (
                            <InputMessage variant="error">
                              {meta.error}
                            </InputMessage>
                          )}
                        </Stack>
                      )}
                    </Field>
                  ) : (
                    <></>
                  )}
                  <Stack spacing="xs">
                    <FormLabel>Image</FormLabel>
                    <ImageDropzone
                      files={filesCatalogImage}
                      setFiles={setFilesCatalogImage}
                    />
                  </Stack>
                </FormWrapper>
              </DialogContent>
              <DialogFooter>
                <Stack
                  display="flex"
                  flexDirection="row"
                  spacing="xs"
                  flex={1}
                  style={{ gap: 15 }}
                >
                  <Button
                    block
                    variant="outline"
                    onClick={() => handleCloseModal()}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isFormLoading}
                    block
                    variant="primary"
                  >
                    {isAddMode ? "Add" : "Update"}
                  </Button>
                </Stack>
              </DialogFooter>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default CatalogEthFormModal;
